// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// store
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// mixpanel
import mixpanel from 'mixpanel-browser';

// posthog
import { usePostHog } from 'posthog-js/react';

// models
import { DirectoryTrackingEvent } from '@karehero/models';

// capacitor
import { Capacitor } from '@capacitor/core';

let environment = import.meta.env.REACT_APP_BUILD_ENVIRONMENT;
if (!environment) environment = 'local';

if (environment !== 'local') {
  mixpanel.init('dda0a5648e3acf682ef6d5c57e336241', {
    track_pageview:
      import.meta.env.NODE_ENV === 'development' ? undefined : 'full-url',
  });
}

const nodeEnv = import.meta.env.NODE_ENV;
const buildEnv = import.meta.env.REACT_APP_BUILD_ENVIRONMENT;
const buildVersion = import.meta.env.REACT_APP_BUILD_VERSION;
const buildHash = import.meta.env.REACT_APP_BUILD_HASH;
const platform = Capacitor.getPlatform();
const environmentProperties = {
  environment: environment,
  nodeEnv: nodeEnv,
  buildEnv: buildEnv,
  buildVersion: buildVersion,
  buildHash: buildHash,
  platform: platform,
};

export enum TrackingForm {
  AttendanceAllowance = 'Attendance Allowance Form',
  FinancialStatus = 'Financial Status',
  CareAssessment = 'Care Assessment',
  EligibilityCalculator = 'Eligibility Calculator',
}

export enum TrackingFormEventType {
  Errored = 'Errored',
  Started = 'Started',
  Viewed = 'Viewed',
  Saved = 'Saved',
  Completed = 'Completed',
  Submitted = 'Submitted',
}

export enum TrackingEvent {
  ArticleViewed = 'Article Viewed',
  ServiceViewed = 'Service Viewed',
  BookCallViewed = 'Book Call Viewed',
  BookCallFailed = 'Book Call Failed',
  ShortlistViewed = 'Shortlist Viewed',
  ShortlistItemSecured = 'Shortlist Item Secured',
  ShortlistSecuredElsewhere = 'Shortlist Secured Elsewhere',
  ActionPlanItemViewed = 'Action Plan Item Viewed',
  ActionPlanItemCSAT = 'Action Plan Item CSAT',
  AttendanceAllowanceEligibilitySubmitted = 'Attendance Allowance Eligibility Submitted',
  AttendanceAllowanceEligibilityResult = 'Attendance Allowance Eligibility Result',
  InternalPartnerReferralFormErrored = 'Internal Partner Referral Form Errored',
  InternalPartnerReferralFormViewed = 'Internal Partner Referral Form Viewed',
  InternalPartnerReferralFormSubmitted = 'Internal Partner Referral Form Submitted',
  AuthenticatedAndroid = 'Authenticated Android',
  AuthenticatedIOS = 'Authenticated iOS',
  AuthenticatedBrowser = 'Authenticated Browser',
  ExternalPartnerReferralFormErrored = 'External Partner Referral Form Errored',
  ExternalPartnerReferralFormViewed = 'External Partner Referral Form Viewed',
  ExternalPartnerReferralFormSubmitted = 'External Partner Referral Form Submitted',
  CareAssessmentSubmitted = 'Care Assessment Submitted',
  AttendanceAllowanceSubmitted = 'Attendance Allowance Submitted',
  FinancialStatusSubmitted = 'Financial Status Submitted',
  APIRequestFromPWA = 'API Request From PWA',
  DirectoryViewed = 'Directory Viewed',
  JoinCommunity = 'Join Community',
  ExternalUrlOpened = 'External URL Opened',

  // Organiser — Adoption & Reach
  OrganiserViewed = 'Organiser Viewed',
  OrganiserDashboardCTAClicked = 'Organiser Dashboard CTA Clicked',
  OrganiserEmpty = 'Organiser Empty',

  // Organiser — Task Lifecycle
  OrganiserEntryCreated = 'Organiser Entry Created',
  OrganiserSeriesCreated = 'Organiser Series Created',
  OrganiserEntryCompleted = 'Organiser Entry Completed',
  OrganiserEntryReopened = 'Organiser Entry Reopened',
  OrganiserEntryMarkedWontDo = "Organiser Entry Marked Won't Do",
  OrganiserEntryDeleted = 'Organiser Entry Deleted',
  OrganiserSeriesDeleted = 'Organiser Series Deleted',
  OrganiserExceptionCreated = 'Organiser Exception Created',

  // Organiser — Editing & Engagement Depth
  OrganiserEntryEdited = 'Organiser Entry Edited',
  OrganiserSeriesEdited = 'Organiser Series Edited',
  OrganiserDialogOpened = 'Organiser Dialog Opened',
  OrganiserDialogCancelled = 'Organiser Dialog Cancelled',
  OrganiserCareCircleFilterChanged = 'Organiser Care Circle Filter Changed',
  OrganiserShowMoreClicked = 'Organiser Show More Clicked',
  OrganiserHideCompletedToggled = 'Organiser Hide Completed Toggled',

  // Shortlist management (admin)
  ShortlistItemAdded = 'Shortlist Item Added',
  ShortlistItemRemoved = 'Shortlist Item Removed',
  ShortlistItemStatusChanged = 'Shortlist Item Status Changed',
  ShortlistItemDismissed = 'Shortlist Item Dismissed',
  ShortlistItemRestored = 'Shortlist Item Restored',
  ShortlistItemNoteSaved = 'Shortlist Item Note Saved',
  ShortlistProviderSearched = 'Shortlist Provider Searched',

  // Backtests (admin)
  BacktestCreated = 'Backtest Created',
  BacktestRunRequested = 'Backtest Run Requested',

  // Documents
  DocumentUploadStarted = 'Document Upload Started',
  DocumentUploaded = 'Document Uploaded',
  DocumentRemoved = 'Document Removed',
  AudioRecordingStarted = 'Audio Recording Started',
  AudioRecordingCompleted = 'Audio Recording Completed',
  AudioRecordingUploaded = 'Audio Recording Uploaded',

  // Onboarding v3 (conversational)
  OnboardingV3StepAnswered = 'Onboarding V3 Step Answered',
  OnboardingV3Completed = 'Onboarding V3 Completed',
  OnboardingV3SummaryConfirmed = 'Onboarding V3 Summary Confirmed',
  OnboardingV3SummaryCorrected = 'Onboarding V3 Summary Corrected',

  // Onboarding v4 (one conversation)
  OnboardingV4BeatFinished = 'Onboarding V4 Beat Finished',
  OnboardingV4QuestionAnswered = 'Onboarding V4 Question Answered',
  OnboardingV4StoppedUnder18 = 'Onboarding V4 Stopped Under 18',
  OnboardingV4AgeChanged = 'Onboarding V4 Age Changed',
  OnboardingV4SummaryConfirmed = 'Onboarding V4 Summary Confirmed',
  OnboardingV4SummaryCorrected = 'Onboarding V4 Summary Corrected',
  OnboardingV4Completed = 'Onboarding V4 Completed',

  // Dashboard
  DashboardCardClicked = 'Dashboard Card Clicked',
  DashboardBookCallClicked = 'Dashboard Book Call Clicked',
  DashboardChatClicked = 'Dashboard Chat Clicked',
}

export type AllTrackingEvents = TrackingEvent | DirectoryTrackingEvent;

const legacyEventMap: { [key: string]: string } = {
  'Care Assessment Viewed': 'Care Plan Form Viewed',
  'Care Assessment Completed': 'Care Plan Complete',
  'Financial Status Complete': 'Financial Status Completed',
  'Attendance Allowance Form Submitted': 'Attendance Allowance Submitted',
};

const rewriteLegacyEvent = (ev: string) => legacyEventMap[ev] || ev;

// track tracks an event to Mixpanel and (optionally) PostHog
export const track = (
  trackingEvent: AllTrackingEvents,
  props?: any,
  accountProperties?: any,
  posthog?: ReturnType<typeof usePostHog> | null,
) => {
  if (!environment || environment === 'local') return;
  mixpanel.track(trackingEvent, {
    environment,
    ...props,
    ...accountProperties,
  });
  if (posthog && posthog.capture) {
    posthog.capture(trackingEvent, props);
    console.log(`[posthog] tracked event: ${trackingEvent}`);
  }
};

/**
 * useTracking is a hook that handles user identification and event tracking
 * for both Mixpanel and PostHog.
 */
export const useTracking = () => {
  // state
  const [isIdentified, setIsIdentified] = useState(false);

  // hooks
  const account = useSelector(getAccount);
  const posthog = usePostHog();

  // memos
  const accountProperties = useMemo(() => {
    if (!account) return {};
    const accountCopy = JSON.parse(JSON.stringify(account));
    return {
      ...accountCopy,
      actualId: accountCopy.id,
      id: accountCopy.email,
      $email: accountCopy.email,
      $name:
        (accountCopy.firstName || 'No name') +
        ' ' +
        (accountCopy.lastName || ''),
    };
  }, [account]);

  // Mixpanel identification
  useEffect(() => {
    if (!environment || environment === 'local') return;
    if (!account || !account.email) {
      setIsIdentified(false);
      return;
    }
    mixpanel.identify(account.email);
    mixpanel.people.set(accountProperties);
    setIsIdentified(true);
  }, [account, accountProperties]);

  useEffect(() => {
    if (!environment || environment === 'local') return;
    mixpanel.register({ ...accountProperties, ...environmentProperties });
  }, [accountProperties]);

  useEffect(() => {
    if (!environment || environment === 'local') return;
    if (!posthog) return;
    posthog.register({ ...environmentProperties });
  }, [posthog]);

  // PostHog identification
  useEffect(() => {
    if (!environment || environment === 'local') return;
    if (!account || !account.id || !posthog) return;

    posthog.identify(account.id, {
      email: account?.email,
      kindeEmail: account?.kindeEmail,
      name:
        ((account?.firstName || '') + ' ' + (account?.lastName || '')).trim() ||
        undefined,
      firstName: account?.firstName,
      lastName: account?.lastName,
      accountId: account?.id,
      accountRole: account?.accountRole?.name,
      organizationId: account?.organization?.id,
      organizationName: account?.organization?.name,
      organizationType: account?.organization?.organizationTypeId,
      isVerified: account?.isVerified,
      isOnboarded: account?.isOnboarded,
    });
  }, [account, posthog]);

  // methods
  const reset = useCallback(() => {
    if (!environment || environment === 'local') return;
    mixpanel?.reset?.();
    posthog?.reset?.();
    setIsIdentified(false);
  }, [posthog]);

  const trackEvent = useCallback(
    (trackingEvent: AllTrackingEvents, props: any) => {
      if (!environment || environment === 'local') return;
      track(trackingEvent, props, accountProperties, posthog);
    },
    [accountProperties, posthog],
  );

  const trackForm = useCallback(
    (
      trackingForm: TrackingForm,
      formEventType: TrackingFormEventType,
      props: any,
    ) => {
      if (!environment || environment === 'local') return;
      const ev = rewriteLegacyEvent(`${trackingForm} ${formEventType}`);

      trackEvent(ev as TrackingEvent, props);
    },
    [trackEvent],
  );

  return {
    isIdentified,
    reset,
    trackForm,
    track: trackEvent,
  };
};
